import React from 'react';
import styled, { css } from 'styled-components';
import { crTheme } from '@comicrelief/component-library';

export const CheckboxWrapperBase = styled.div<{
  checked?: boolean;
}>`
  margin-top: 1rem;
  border-radius: 0.5rem;
  background-color: ${crTheme.color('grey_light')};
  color: ${crTheme.color('black')};
  border: 1px solid ${crTheme.color('grey')};

  ${({ checked }) => checked && css`
    color: ${crTheme.color('white')};
    background-color: ${crTheme.color('blue_donate')};
    border-color: ${crTheme.color('white')};
  `}
`;

export const CheckboxContainer = styled.div<{
  checkboxPlacement?: 'left' | 'right';
}>`
  display: flex;
  align-items: center;
  padding: 12px;

  ${({ checkboxPlacement }) => checkboxPlacement === 'right' && css`
    flex-direction: row-reverse;
    padding: 0.75rem 0.75rem 0.75rem 0.5rem;
  `}
`;

export const CheckboxVisual = styled.span<{
  checked?: boolean;
  $checkboxPlacement?: 'left' | 'right';
}>`
  ${({ $checkboxPlacement }) => ($checkboxPlacement === 'right' ? 'margin-left: 12px;' : 'margin-right: 12px;')}
  width: 24px;
  height: 24px;
  border-radius: 0.25rem;
  flex-shrink: 0;
  position: relative;
  background-color: ${crTheme.color('white')};
  border: 1px solid ${crTheme.color('grey')};

  ${({ checked }) => checked && css`
    background-color: white;
    border-color: ${crTheme.color('white')};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      mask: url("/assets/images/tick-white.svg") no-repeat center;
      mask-size: contain;
      -webkit-mask: url("/assets/images/tick-white.svg") no-repeat center;
      -webkit-mask-size: contain;
      background-color: ${crTheme.color('blue_donate')};
    }
  `}
`;

export const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  /* This input is not visible but needs these long winded styles to remain accessible to screen readers */
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

export const CheckboxIconWrapper = styled.div`
  flex-shrink: 0;
  width: 25px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CheckboxContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const CheckboxTextWrapper = styled.div<{ checked?: boolean }>`
  flex: 1;
  font-weight: bold;
  
  ${({ checked }) => checked && css`
    color: ${crTheme.color('white')};
  `}
`;

export const CheckboxIcon = styled.img<{ checked?: boolean }>`
  width: 100%;
  height: auto;
  filter: brightness(0) saturate(100%); /* Makes it black */

  ${({ checked }) => checked && css`
    filter: brightness(0) invert(1); /* Makes it white */
  `}
`;

// Default CheckboxWrapper component for backward compatibility
interface CheckboxWrapperProps {
  checked?: boolean;
  checkboxPlacement?: 'left' | 'right';
  children: React.ReactNode;
}

function CheckboxWrapper({
  checked = false,
  checkboxPlacement = 'left',
  children
}: CheckboxWrapperProps) {
  return (
    <CheckboxWrapperBase checked={checked}>
      <CheckboxContainer checkboxPlacement={checkboxPlacement}>
        <CheckboxVisual checked={checked} $checkboxPlacement={checkboxPlacement} />
        <div style={{ flex: 1 }}>{children}</div>
        <HiddenCheckbox />
      </CheckboxContainer>
    </CheckboxWrapperBase>
  );
}

export default CheckboxWrapper;
